import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Mail, Phone, X } from "lucide-react";
import { adminApi, formatError } from "@/lib/adminApi";

const BADGE = {
  requested: "bg-white/10 text-[#f5f3ef]",
  confirmed: "bg-[#c8a97e] text-[#0a0b0d]",
  cancelled: "bg-white/5 text-[#b4b2ad]",
};

const Row = ({ a, onChange }) => {
  const [date, setDate] = useState(a.preferred_date);
  const [time, setTime] = useState(a.visit_time || (a.time_slot === "Afternoon" ? "14:00" : "10:00"));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setDate(a.preferred_date);
    if (a.visit_time) setTime(a.visit_time);
  }, [a.preferred_date, a.visit_time]);

  const save = async (body, msg) => {
    setBusy(true);
    try {
      onChange(await adminApi.updateAppointment(a.id, body));
      toast.success(msg);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article data-testid={`appt-row-${a.id}`} className="grid grid-cols-1 gap-4 border border-white/10 bg-[#121418] p-5 md:grid-cols-[1fr_auto]">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-display text-2xl">{a.company}</span>
          <span data-testid={`appt-status-${a.id}`} className={`rounded-full px-3 py-1 font-mono text-[11px] uppercase tracking-[0.15em] ${BADGE[a.status] || BADGE.requested}`}>{a.status}</span>
          {a.confirmation_sent && <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-emerald-300">Confirmation + calendar sent</span>}
          {a.reminder_sent && <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-emerald-300">Reminder sent</span>}
        </div>
        <p className="text-sm text-[#d6d3cd]">{a.name}</p>
        <div className="flex flex-wrap gap-4 text-sm text-[#c9c6c0]">
          <a href={`mailto:${a.email}`} className="flex items-center gap-1.5 hover:text-[#c8a97e]"><Mail className="h-3.5 w-3.5" />{a.email}</a>
          {a.phone && <a href={`tel:${a.phone}`} className="flex items-center gap-1.5 hover:text-[#c8a97e]"><Phone className="h-3.5 w-3.5" />{a.phone}</a>}
        </div>
        {Array.isArray(a.brands) && a.brands.length > 0 && <p className="text-xs text-[#c8a97e]">{a.brands.join(" · ")}</p>}
        {a.message && <p className="text-sm italic text-[#b4b2ad]">“{a.message}”</p>}
      </div>
      <div className="flex flex-col gap-3 md:items-end">
        <div className="flex items-center gap-2">
          <input data-testid={`appt-date-${a.id}`} type="date" value={date} onChange={(e) => setDate(e.target.value)} className="field w-40 py-2 text-sm" />
          <input data-testid={`appt-time-${a.id}`} type="time" value={time} onChange={(e) => setTime(e.target.value)} className="field w-28 py-2 text-sm" style={{ colorScheme: "dark" }} />
          <span className="font-mono text-xs uppercase text-[#b4b2ad]">{a.time_slot}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {(date !== a.preferred_date || (a.visit_time && time !== a.visit_time)) && <button data-testid={`appt-save-date-${a.id}`} disabled={busy} onClick={() => save({ preferred_date: date, visit_time: time }, a.status === "confirmed" ? "Updated — new confirmation sent" : "Saved")} className="rounded-full border border-white/20 px-4 py-2 text-[12px] uppercase tracking-[0.14em]">Save changes</button>}
          {a.status !== "confirmed" && <button data-testid={`appt-confirm-${a.id}`} disabled={busy} onClick={() => save({ status: "confirmed", preferred_date: date, visit_time: time }, "Confirmed — confirmation & calendar invite sent")} className="flex items-center gap-2 rounded-full bg-[#c8a97e] px-4 py-2 text-[12px] uppercase tracking-[0.14em] text-[#0a0b0d]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Confirm</button>}
          {a.status !== "cancelled" && <button data-testid={`appt-cancel-${a.id}`} disabled={busy} onClick={() => save({ status: "cancelled" }, "Cancelled — no reminder will be sent")} className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-[12px] uppercase tracking-[0.14em]"><X className="h-4 w-4" /> Cancel</button>}
        </div>
      </div>
    </article>
  );
};

export const AppointmentsPanel = () => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    adminApi.appointments().then(setItems).catch((err) => { toast.error(formatError(err)); setItems([]); });
  }, []);
  const onChange = (u) => setItems((xs) => xs.map((x) => (x.id === u.id ? u : x)));

  return (
    <div data-testid="admin-appointments" className="mx-auto max-w-5xl px-6 pb-24 pt-8">
      <h1 className="font-display text-5xl sm:text-6xl">Appointments</h1>
      <p className="mt-3 max-w-xl text-sm text-[#c9c6c0]">Set the agreed date and time, then Confirm: the customer instantly gets a confirmation email with an “Add to my calendar” button, and a reminder at 10:00 the day before. Requested or cancelled visits get neither.</p>
      <div className="mt-10 space-y-4">
        {items === null && <Loader2 className="h-6 w-6 animate-spin text-[#c8a97e]" />}
        {items?.length === 0 && <p data-testid="admin-appts-empty" className="text-sm text-[#b4b2ad]">No appointment requests yet.</p>}
        {items?.map((a) => <Row key={a.id} a={a} onChange={onChange} />)}
      </div>
    </div>
  );
};
